// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import { Input, NewsBlockProps } from "../../../types/NewsTypes";
import { HtmlEditor } from "../../../components/HtmlEditor";
import { Button, Divider, Form, Popconfirm } from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  QuestionCircleOutlined,
  UpOutlined
} from "@ant-design/icons/lib";
import { useTranslation } from "react-i18next";
import NewsImageForm from "../details/NewsImageForm";
import { NewsAddBlocButton } from "./NewsAddBlocButton";
import { useStore } from "effector-react";
import { TemplateStore } from "../../../../state/template/TemplateStore";
import {
  TemplateDTO,
  TemplateInputImageDto,
  TemplateInputStringDto,
  TemplateInputTextDto
} from "../../../../utils/htmlTemplate/dto/template.dto";
import FormField from "../../../components/FormField";

export const NewsBlock: React.FunctionComponent<NewsBlockProps> = (
  props: NewsBlockProps
) => {
  const { t } = useTranslation();

  const {
    block,
    indexBlock,
    handleUpdateBlock,
    handleDeleteBlock,
    handleAddBlock,
    handleAscendBlock,
    handleDescendBlock,
    editMode
  } = props;

  const templatesStore: TemplateDTO[] | null = useStore(TemplateStore);

  const handleTextContentChange = (html: string, inputIndex: number) => {
    if (inputIndex !== undefined) {
      block.inputs[inputIndex].content = html;
      handleUpdateBlock(block, indexBlock);
    }
  };

  const handleImgContentChange = (
    base64Url: string,
    type: string,
    inputIndex?: number
  ) => {
    if (inputIndex !== undefined) {
      block.inputs[inputIndex].content = base64Url;
      handleUpdateBlock(block, indexBlock);
    }
  };

  const renderInputFrom = (
    input: Input,
    inputIndex: number,
    template: TemplateDTO
  ) => {
    switch (input.inputType) {
      case "text":
        const textInput = template.format.inputs[
          inputIndex
        ] as TemplateInputTextDto;
        const inputTextProperties = textInput.properties;
        return (
          <Form.Item name="textContent" key={`${indexBlock}:${inputIndex}`}>
            <HtmlEditor
              handleTextContentChange={handleTextContentChange}
              inputIndex={inputIndex}
              inputProperties={inputTextProperties}
              text={input.content}
              editMode={editMode}
            />
          </Form.Item>
        );
      case "string":
        const stringInput = template.format.inputs[
          inputIndex
        ] as TemplateInputStringDto;
        return (
          <Form.Item name="stringContent" key={`${indexBlock}:${inputIndex}`}>
            <FormField
              labelName={stringInput.label}
              defaultValue={input.content}
              required={true}
              emptyErrorMessage={t("global.empty_error_field")}
              readOnly={!editMode}
              onChange={event => {
                handleTextContentChange(event.target.value, inputIndex);
              }}
            />
          </Form.Item>
        );
      case "img":
        const imageInput = template.format.inputs[
          inputIndex
        ] as TemplateInputImageDto;
        const inputimageProperties = imageInput.properties;
        return (
          <NewsImageForm
            key={"inputImage" + indexBlock + inputIndex}
            formFieldName={`imageBlock${indexBlock}Input${inputIndex}`}
            formFieldLabel={t(
              inputimageProperties?.maxWeight
                ? "news.block.image_with_max_weight"
                : "news.block.image",
              {
                index: indexBlock + 1,
                templateName: template.name,
                maxWeight: inputimageProperties?.maxWeight
                  ? Math.floor(inputimageProperties.maxWeight / 1024)
                  : undefined
              }
            )}
            inputIndex={inputIndex}
            inputProperties={inputimageProperties}
            onChangeCallBack={handleImgContentChange}
            base64Url={input.content}
            editMode={editMode}
          />
        );
    }
  };

  const template: TemplateDTO | undefined = templatesStore.find(
    template => template.id === block.templateId
  );

  return (
    <>
      {!!template && (
        <>
          <div
            id={`nbo-block${indexBlock}`}
            className="d-flex flex-row justify-content-end align-items-stretch flex-wrap nbo-block"
          >
            <div className="flex-grow-1 d-flex align-items-center">
              <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
                <h4 className="text-primary mb-0">
                  {t("news.add_block.title", {
                    index: indexBlock + 1
                  })}{" "}
                  : {template.name}
                </h4>
              </Divider>
            </div>
            {editMode && (
              <div className="d-flex align-items-center divider-sm-transparent">
                <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
                  {handleAscendBlock && (
                    <Button
                      htmlType="button"
                      className="btn-secondary m-2"
                      onClick={() => handleAscendBlock(indexBlock)}
                    >
                      <UpOutlined /> {t("news.global_buttons.block.up")}
                    </Button>
                  )}
                  {handleDescendBlock && (
                    <Button
                      htmlType="button"
                      className="btn-secondary m-2"
                      onClick={() => handleDescendBlock(indexBlock)}
                    >
                      <DownOutlined /> {t("news.global_buttons.block.down")}
                    </Button>
                  )}
                  <NewsAddBlocButton
                    buttonLabel={t("news.global_buttons.block.insert")}
                    handleAddBlock={handleAddBlock}
                    index={indexBlock}
                  />
                  <Popconfirm
                    title={t("news.add_block.cancel_confirmation")}
                    okText={t("global.yes")}
                    cancelText={t("global.no")}
                    onConfirm={() => handleDeleteBlock(indexBlock)}
                    placement="top"
                    icon={<QuestionCircleOutlined />}
                  >
                    <Button htmlType="reset" className="btn-danger m-2">
                      <DeleteOutlined /> {t("news.add_block.delete")}
                    </Button>
                  </Popconfirm>
                </Divider>
              </div>
            )}
          </div>
          {block.inputs.map((input, index) =>
            renderInputFrom(input, index, template)
          )}
        </>
      )}
    </>
  );
};

NewsBlock.defaultProps = {
  editMode: true
};
